import { useTranslation } from 'react-i18next'
import useSWR from 'swr'

import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { MonetaBankCredentialsForm } from '@/forms/MonetaBankCredentialsForm/MonetaBankCredentialsForm'
import API from '@/lib/api'

import { AvailableAccounts, BankConnectLayout, ConnectionNotice } from '../components/BankConnectLayout'

export default function MonetaBankPage() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'bank',
  })

  const { data, isLoading, mutate } = useSWR<{
    data: {
      credentials: boolean
      connection: 'n/a' | 'failed' | 'success'
      availableAccounts: {
        accountId: number
        accountName: string
        accountNumber: string
        accountNumberPrefix: string | null
        bankCode: string
        iban: string
        bankBicCode: string | null
        friendlyName: string | null
        accountTypeId: string
        mainCurrency: string | null
      }[]
      knownAccounts: string[]
    }
  }>('/api/v2/moneta-bank', apiFetcher)

  useSetPageHeader({
    breadcrumbs: [
      {
        href: '/bank',
        children: t('title'),
      },
      {
        href: '/bank/moneta',
        children: t('moneta.title'),
      },
    ],
    newButton: 'invoice',
  })

  return (
    <>
      <title>{`${t('title')}, ${t('moneta.title')}`}</title>

      <BankConnectLayout
        service="moneta"
        title="Propojit Monetu"
        description="Účet propojíte pasivním API tokenem, který pohyby jen čte."
        steps={[
          <>V internetovém bankovnictví (ne v mobilní aplikaci) otevřete Nastavení, Správa API tokenů a zvolte Vytvořit token.</>,
          <>Vyberte účet, který chcete propojit.</>,
          <>Token pojmenujte, například "invo.space", ať víte, kde ho používáte.</>,
          <>Práva tokenu nechte na "Pasivní - pouze náhled".</>,
          <>Zaškrtněte "Automaticky prodlužovat token", aby nevypršel.</>,
          <>Potvrďte vytvoření tokenu a vložte ho do formuláře. Potom níže přidejte účty, které chcete sledovat.</>,
        ]}
        accounts={
          <AvailableAccounts
            loading={isLoading}
            accounts={data?.data.availableAccounts}
            knownAccounts={data?.data.knownAccounts}
            emptyText={data?.data.credentials ? 'Token zatím nezpřístupnil žádné účty.' : 'Po uložení tokenu se tu ukážou účty, ke kterým má přístup.'}
            onAdd={(accountId) =>
              API.post(`/api/v2/moneta-bank/accounts/${accountId}`, {
                accountId,
              }).then(() => mutate())
            }
          />
        }
      >
        <h2 className="mb-1 text-sm font-medium">{data?.data.credentials ? 'Token je uložený' : 'Propojení tokenem'}</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          {data?.data.credentials ? 'Novým tokenem nahradíte ten současný, třeba když vyprší.' : 'Token vložte celý, včetně všech znaků.'}
        </p>
        <ConnectionNotice connection={data?.data.connection} />
        <MonetaBankCredentialsForm onSuccess={() => mutate()} />
      </BankConnectLayout>
    </>
  )
}
